import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { matchesSearchTerms, normalizeCode, normalizeSearch, searchRank, searchTerms, variantLabel } from '@pos/core';
import type { VariantRow } from '@pos/local-db';
import { Input, cx } from '../../ui/index.tsx';
import { SearchIcon } from '../../ui/icons.tsx';
import type { Catalog, CatalogItem } from './data.ts';

export interface PickedVariant {
  item: CatalogItem;
  variant: VariantRow;
  label: string;
}

/** "3*7790895000997" → cantidad 3 y el resto como búsqueda. */
export function splitQtyPrefix(query: string): { qty: number | null; rest: string } {
  const m = /^\s*(\d+(?:[.,]\d{1,3})?)\s*\*\s*(.+)$/.exec(query);
  if (!m) return { qty: null, rest: query };
  const qty = Number(m[1]!.replace(',', '.'));
  return qty > 0 ? { qty, rest: m[2]! } : { qty: null, rest: query };
}

/** Cuántos resultados se muestran como máximo (alcanza para ver todos los de una búsqueda normal). */
export const MAX_RESULTS = 100;

/**
 * Buscador de productos por nombre o código. Un lector de códigos USB "tipea" el código y
 * Enter: si coincide exacto con un código de barras o interno, se elige directo.
 * Con qtyPrefix, "3*código" elige el producto con cantidad 3.
 */
export function ProductPicker({
  catalog,
  onPick,
  placeholder = 'Buscá por nombre o escaneá el código…',
  autoFocus,
  onlyActive = true,
  qtyPrefix = false,
  renderMeta,
  inputRef,
  onEmptyEnter,
  onEmptyArrow,
}: {
  catalog: Catalog;
  /** qty: cantidad escrita con el prefijo "3*" (en unidades del producto), o null. */
  onPick: (picked: PickedVariant, qty: number | null) => void;
  placeholder?: string;
  autoFocus?: boolean;
  onlyActive?: boolean;
  qtyPrefix?: boolean;
  /** Dato extra a la derecha de cada resultado (p. ej. precio y stock). */
  renderMeta?: (option: PickedVariant) => ReactNode;
  inputRef?: RefObject<HTMLInputElement | null>;
  /** Enter con el buscador vacío (en el punto de venta: cobrar). */
  onEmptyEnter?: () => void;
  /** Flechas con el buscador vacío (en el punto de venta: ir a los productos de la venta). */
  onEmptyArrow?: (dir: 1 | -1) => void;
}) {
  const [query, setQuery] = useState('');
  const lastEnter = useRef(0);
  const [highlight, setHighlight] = useState(0);
  const [notFound, setNotFound] = useState<string | null>(null);
  const ownRef = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const input = inputRef ?? ownRef;
  const { qty, rest } = qtyPrefix ? splitQtyPrefix(query) : { qty: null, rest: query };

  const options = useMemo(() => {
    const all: PickedVariant[] = catalog.items
      .filter((i) => !onlyActive || i.product.active)
      .flatMap((item) =>
        item.variants
          .filter((v) => !onlyActive || v.active)
          .map((variant) => {
            const attrs = variantLabel(variant.attributes);
            return { item, variant, label: attrs ? `${item.product.name} · ${attrs}` : item.product.name };
          }),
      );
    return all;
  }, [catalog, onlyActive]);

  // Todas las coincidencias: primero las que empiezan con lo buscado, después las que tienen una
  // palabra que empieza así y al final el resto; dentro de cada grupo, por nombre.
  const { results, more } = useMemo(() => {
    const terms = searchTerms(rest);
    if (terms.length === 0) return { results: [], more: 0 };
    const found = options
      .filter((o) =>
        matchesSearchTerms(normalizeSearch(`${o.label} ${o.variant.barcode ?? ''} ${o.variant.sku ?? ''} ${o.item.category?.name ?? ''}`), terms),
      )
      .map((o, i) => ({ o, i, rank: searchRank(o.label, terms) }))
      .sort((a, b) => a.rank - b.rank || a.i - b.i)
      .map((r) => r.o);
    return { results: found.slice(0, MAX_RESULTS), more: Math.max(0, found.length - MAX_RESULTS) };
  }, [options, rest]);

  // Con las flechas, el resultado marcado queda siempre a la vista.
  useEffect(() => {
    list.current?.children[highlight]?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const pick = (picked: PickedVariant) => {
    onPick(picked, qty);
    setQuery('');
    setHighlight(0);
    setNotFound(null);
    input.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && onEmptyArrow && !rest.trim()) {
      e.preventDefault();
      onEmptyArrow(e.key === 'ArrowDown' ? 1 : -1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const now = performance.now();
      const quick = now - lastEnter.current < 200;
      lastEnter.current = now;
      if (!rest.trim()) {
        // No si se mantiene apretado ni si llega pegado a otro Enter: algunos lectores de códigos
        // mandan dos al final del código y abrirían el cobro solos.
        if (onEmptyEnter && !e.repeat && !quick) onEmptyEnter();
        return;
      }
      const code = normalizeCode(rest);
      const exact = options.find((o) => o.variant.barcode === code || (o.variant.sku && normalizeCode(o.variant.sku) === code));
      if (exact) return pick(exact);
      if (results[highlight]) return pick(results[highlight]!);
      if (rest.trim()) setNotFound(rest.trim());
    } else if (e.key === 'Escape') {
      setQuery('');
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-5 -translate-y-1/2 text-slate-400" />
        <Input
          ref={input}
          type="search"
          autoFocus={autoFocus}
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlight(0);
            setNotFound(null);
          }}
          onKeyDown={onKeyDown}
          aria-label="Buscar producto"
          aria-autocomplete="list"
          className="pl-10"
        />
      </div>
      {notFound && <p className="mt-1 text-sm text-red-600">No hay ningún producto con “{notFound}”.</p>}
      {results.length > 0 && (
        <ul ref={list} role="listbox" className="absolute z-20 mt-1 max-h-80 w-full overflow-y-auto rounded-xl bg-white p-1 shadow-raised ring-1 ring-slate-200">
          {results.map((r, i) => (
            <li key={r.variant.id} role="option" aria-selected={i === highlight}>
              <button
                type="button"
                className={cx('flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm', i === highlight ? 'bg-slate-100' : 'hover:bg-slate-50')}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => pick(r)}
              >
                <span className="min-w-0 truncate">{r.label}</span>
                {renderMeta ? (
                  <span className="shrink-0 text-right text-xs text-slate-500">{renderMeta(r)}</span>
                ) : (
                  <span className="shrink-0 text-xs text-slate-500">{r.variant.barcode ?? r.variant.sku ?? ''}</span>
                )}
              </button>
            </li>
          ))}
          {more > 0 && <li className="px-3 py-2 text-xs text-slate-500">Y {more} más: escribí otra palabra para achicar la búsqueda.</li>}
        </ul>
      )}
    </div>
  );
}
